import React, { useCallback, useState } from 'react';
import type { PickerResponse } from 'filestack-js';
import { useFilestack } from '@shared/utils/filestack';
import type { FilestackCloseInfo } from '@shared/utils/filestack';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { resolveIsWidgetEnabled } from './resolveIsWidgetEnabled';
import type { PhotoUploadUseCase } from '@shared/utils/photoUpload';
import { LazyUploadWidgetModal } from './UploadWidgetModal.lazy';
import type { UploadedItem } from './UploadWidgetModal.types';

/**
 * A single uploaded image, normalized across both uploaders so callers don't care which one ran.
 *
 * - `uploadService` already registered the Photo → `photoId` is set; `url` is the canonical media URL.
 * - `filestack` only uploaded the bytes → `photoId` is absent; `url` is a previewable Filestack URL the
 *   caller still registers/sets however it already does.
 *
 * `width`/`height` are read from the image for both sources; `mimeType`/`filename` are best-effort.
 */
export type UploadedImage = Readonly<{
  url: string;
  photoId?: string;
  filename?: string;
  mimeType?: string;
  width?: number;
  height?: number;
  fileSize?: number;
  source: 'filestack' | 'uploadService';
}>;

type UseImageUploadArgs = Readonly<{
  /** Filestack storage container — typically the event's firebaseId (ignored by the upload-service branch). */
  containerId: string;
  accept?: string | string[];
  maxFiles?: number;
  /** Called once per completed batch with the normalized image(s). Single-image callers use `images[0]`. */
  onUpload: (images: UploadedImage[]) => void | Promise<void>;
  /**
   * The upload did not produce a usable image: the picker failed outright, or normalizing/handing off
   * the result threw. Kept to those two cases so callers can treat it as "the upload failed" and, say,
   * reopen the picker to retry — note a throw from your own `onUpload` also lands here, so catch your
   * own save errors inside `onUpload` if you don't want them read as an upload failure.
   */
  onError?: (error: unknown) => void;
  /**
   * Called when the picker/widget is dismissed, without or after uploading. Filestack reports what happened
   * in the session (`info`); the upload service widget reports nothing, so `info` is undefined on that branch.
   */
  onClose?: (info?: FilestackCloseInfo) => void;
  /**
   * Forces the crop to this width/height ratio (1 for square) on both branches: the widget locks its editor
   * to it, and Filestack gets it as a forced crop transformation, the one the registry gift forms used.
   */
  cropAspectRatio?: number;
  /**
   * Surface identity. The upload-service widget only turns on for allowlisted `subArea`s
   * (see `WIDGET_ENABLED_SUB_AREAS`) via `resolveIsWidgetEnabled`; omit it to stay on Filestack.
   * Without this gate the global flag leaks the widget onto every surface and 400s the ones whose
   * request body doesn't match the schema.
   */
  useCase?: PhotoUploadUseCase;
  /** Skips Filestack's on-mount warm-up, for a surface whose picker may never open. Ignored by the widget. */
  deferPreparation?: boolean;
  /** Stacking layer for the widget, for a caller that opens it from above the modal layer. */
  zIndex?: number | string;
  /** Hides the widget's Phone source. Phone uploads always save to the event's photo library, not to this caller. */
  hidePhoneSource?: boolean;
}>;

const MIME_BY_EXT: Readonly<Record<string, string>> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  tiff: 'image/tiff',
  tif: 'image/tiff',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
};

const stripQuery = (url: string): string => url.split('?')[0];
const mimeFromUrl = (url: string): string | undefined => {
  const ext = stripQuery(url).split('.').pop()?.toLowerCase();
  return ext ? MIME_BY_EXT[ext] : undefined;
};

/**
 * The name a backend resolves the uploaded blob by inside the event's media container. Filestack names the
 * blob by the picked filename; the upload service names it by the canonical URL's last segment (it may
 * disambiguate a duplicate or change the extension when it transcodes), decoded here because the backends
 * re-encode it when they rebuild the URL.
 */
export const uploadedImageAssetId = (image: UploadedImage): string =>
  image.source === 'filestack' ? (image.filename ?? '') : decodeURIComponent(stripQuery(image.url).split('/').pop() ?? '');

/** Read an image's pixel dimensions from its URL. Resolves `{}` on any failure (or non-browser env). */
const loadDimensions = (src: string): Promise<{ width?: number; height?: number }> =>
  new Promise(resolve => {
    if (!src || typeof window === 'undefined' || typeof window.Image === 'undefined') {
      resolve({});
      return;
    }
    const img = new window.Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({});
    img.src = src;
  });

/**
 * One uploader behind one interface. Reads the `joyUploadWidgetEnabled` flag:
 * when off it opens the legacy **Filestack** picker; when on it opens the new **upload service** widget. Either way
 * the caller gets the same normalized {@link UploadedImage}[] via `onUpload`, so migrating a Filestack site
 * is: swap `useFilestack` → `useImageUpload`, drop the site's own `uploadPhotoFromUrl`, render `uploadWidget`.
 *
 * Each Filestack surface migrates by swapping to this hook; doc/plans/filestack-replacement/README.md tracks
 * which have. The widget branch only turns on for an allowlisted `useCase.subArea`, so a caller that passes
 * none stays on Filestack even with the flag on.
 */
export const useImageUpload = ({
  containerId,
  accept = 'image/*',
  maxFiles = 1,
  onUpload,
  onError,
  onClose,
  useCase,
  cropAspectRatio,
  deferPreparation,
  zIndex,
  hidePhoneSource,
}: UseImageUploadArgs) => {
  const [flagEnabled] = useIsFeatureEnabled('joyUploadWidgetEnabled');
  // Gate on the subArea allowlist (resolveIsWidgetEnabled), not the raw flag: a surface reaches the
  // upload-service widget only when its `useCase.subArea` is allowlisted (see `WIDGET_ENABLED_SUB_AREAS`).
  // Flag-only gating leaks the widget onto every surface and 400s the ones that don't match the schema.
  const useNewUploadWidget = resolveIsWidgetEnabled({ flagEnabled, subArea: useCase?.subArea });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // --- Legacy Filestack branch ---
  const { open: openFilestack, getPhotoPreviewUrl } = useFilestack({
    containerId,
    accept,
    maxFiles,
    onClose,
    // With the widget on, Filestack is never opened here, so its warm-up would be wasted.
    deferPreparation: deferPreparation || useNewUploadWidget,
    // A surface that forces a crop needs it on this branch too, or the fallback saves uncropped images.
    transformations: cropAspectRatio === undefined ? undefined : { crop: { aspectRatio: cropAspectRatio, force: true } },
    // A total failure short-circuits inside useFilestack and never reaches onUploadDone, so without this
    // the caller sees the picker vanish with no error and no callback — the silent-failure class of bug.
    onCompleteUploadFailure: () => {
      setIsUploading(false);
      onError?.(new Error('Photo upload failed'));
    },
    onUploadDone: async (res: PickerResponse) => {
      setIsUploading(true);
      try {
        const images: UploadedImage[] = await Promise.all(
          res.filesUploaded.map(async file => {
            const url = await getPhotoPreviewUrl({ url: file.url });
            const { width, height } = await loadDimensions(url);
            return {
              url,
              filename: file.filename,
              mimeType: file.mimetype,
              fileSize: file.size,
              width,
              height,
              source: 'filestack' as const,
            };
          }),
        );
        if (images.length > 0) await onUpload(images);
      } catch (error) {
        onError?.(error);
      } finally {
        setIsUploading(false);
      }
    },
  });

  // --- New upload-service branch ---
  const handleUploadServiceComplete = useCallback(
    async (items: UploadedItem[]) => {
      setIsUploading(true);
      try {
        const images: UploadedImage[] = await Promise.all(
          items
            .filter(item => item.blobUrl || item.photoId)
            .map(async item => {
              const url = item.blobUrl ?? '';
              const { width, height } = await loadDimensions(url);
              return {
                url,
                photoId: item.photoId,
                filename: item.filename,
                // A remote pick (Unsplash, pasted link) carries no bytes, so the widget reports no type for
                // it. The extension is the only clue left for those.
                mimeType: item.mimeType ?? (url ? mimeFromUrl(url) : undefined),
                fileSize: item.fileSize,
                width,
                height,
                source: 'uploadService' as const,
              };
            }),
        );
        if (images.length > 0) await onUpload(images);
        // Closing here rather than in `finally`: on failure the modal must stay open. Closing it throws
        // away the staged files and leaves the caller with only a toast, and a caller that tries to
        // reopen from `onError` loses the race with this handler. Staying open is the retry affordance.
        setIsModalOpen(false);
        // Filestack reports close after a successful upload too; callers key their "did a photo land" logic
        // on that (PagePhotoSettings), so the widget does the same.
        onClose?.();
        return { linked: images.length };
      } catch (error) {
        // Report zero linked: the caller's onError shows its own failure toast, and the widget sizes its
        // success toast to this, so a failed save doesn't also read "1 photo uploaded".
        onError?.(error);
        return { linked: 0 };
      } finally {
        setIsUploading(false);
      }
    },
    [onUpload, onError, onClose],
  );

  const open = useCallback(() => {
    if (useNewUploadWidget) {
      setIsModalOpen(true);
    } else {
      openFilestack();
    }
  }, [useNewUploadWidget, openFilestack]);

  // The upload service widget is a modal component, so we hand it back for the caller to render. Null on the
  // Filestack branch (it opens its own picker imperatively).
  const uploadWidget = useNewUploadWidget ? (
    <LazyUploadWidgetModal
      isOpen={isModalOpen}
      onClose={() => {
        setIsModalOpen(false);
        onClose?.();
      }}
      onUploadComplete={handleUploadServiceComplete}
      maxFiles={maxFiles}
      cropAspectRatio={cropAspectRatio}
      zIndex={zIndex}
      hidePhoneSource={hidePhoneSource}
    />
  ) : null;

  // isOpen is the widget's only: Filestack opens its own overlay, which this hook does not observe.
  return { open, uploadWidget, isUploading, isOpen: useNewUploadWidget && isModalOpen };
};
